import React, { createContext, useContext, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Curated font set (Google Fonts). `g` is the css2 `family=` param.
export const FONTS = [
  { key: "manrope", name: "Manrope", stack: "'Manrope', 'Inter', sans-serif", g: "Manrope:wght@400;500;600;700" },
  { key: "inter", name: "Inter", stack: "'Inter', sans-serif", g: "Inter:wght@400;500;600;700" },
  { key: "sora", name: "Sora", stack: "'Sora', sans-serif", g: "Sora:wght@400;500;600;700" },
  { key: "space_grotesk", name: "Space Grotesk", stack: "'Space Grotesk', sans-serif", g: "Space+Grotesk:wght@400;500;600;700" },
  { key: "plus_jakarta", name: "Plus Jakarta Sans", stack: "'Plus Jakarta Sans', sans-serif", g: "Plus+Jakarta+Sans:wght@400;500;600;700" },
  { key: "dm_sans", name: "DM Sans", stack: "'DM Sans', sans-serif", g: "DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700" },
  { key: "outfit", name: "Outfit", stack: "'Outfit', sans-serif", g: "Outfit:wght@400;500;600;700" },
  { key: "fraunces", name: "Fraunces", stack: "'Fraunces', Georgia, serif", g: "Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700" },
];
export const DEFAULT_FONT = "manrope";
export const DEFAULT_SIZE = 16;

// Public contact details (admin-configurable via Platform Settings). These are
// the fallback defaults; the live values come from /api/public/platform-config.
export const CONTACT_DEFAULTS = {
  support_email: "support@2degreesconnections.com",
  support_phone: "(763) 913-6368",
  company_address: "2909 Wayzata Blvd STE 70689, Minneapolis, MN 55405",
};
const mergeContact = (c) => ({ ...CONTACT_DEFAULTS, ...Object.fromEntries(Object.entries(c || {}).filter(([, v]) => v)) });

// Site-wide announcement banner defaults (admin toggles `enabled`).
export const ANNOUNCEMENT_DEFAULTS = {
  enabled: false,
  message: "We're submitting to the app stores! You may see a few bugs, let us know if you do.",
  link_text: "let us know if you do",
  link_url: "",
};

// Apply a typography config to the live document (used for save + live preview).
export function applyTypography(typo) {
  const font = FONTS.find((f) => f.key === (typo?.font_key || DEFAULT_FONT)) || FONTS[0];
  let link = document.getElementById("app-font-link");
  if (!link) { link = document.createElement("link"); link.id = "app-font-link"; link.rel = "stylesheet"; document.head.appendChild(link); }
  const href = `https://fonts.googleapis.com/css2?family=${font.g}&display=swap`;
  if (link.href !== href) link.href = href;
  document.documentElement.style.setProperty("--app-font-sans", font.stack);
  document.documentElement.style.setProperty("--app-font-base-size", `${typo?.base_size || DEFAULT_SIZE}px`);
}

const Ctx = createContext({ config: { nav_labels: {}, typography: {} }, navLabel: (_ns, _k, fb) => fb, contact: CONTACT_DEFAULTS, announcement: ANNOUNCEMENT_DEFAULTS });

export function PlatformConfigProvider({ children }) {
  const { data } = useQuery({
    queryKey: ["platform-config"],
    queryFn: async () => { const r = await fetch(`${BACKEND}/api/public/platform-config`); return r.ok ? r.json() : { nav_labels: {}, typography: {} }; },
    staleTime: 5 * 60_000,
  });
  const config = data || { nav_labels: {}, typography: {} };
  const contact = mergeContact(config.contact);
  const announcement = { ...ANNOUNCEMENT_DEFAULTS, ...(config.announcement || {}) };

  useEffect(() => { applyTypography(config.typography); }, [config.typography]);

  const navLabel = (ns, key, fallback) => config.nav_labels?.[`${ns}.${key}`] || fallback;
  return <Ctx.Provider value={{ config, navLabel, contact, announcement }}>{children}</Ctx.Provider>;
}

export const usePlatformConfig = () => useContext(Ctx);
